import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { formatSmileSimulationDateTime } from "../lib/session";
import { getSmileSimulationCopy } from "../model/i18n";
import type { SmileSimulationLanguage } from "../model/types";
import { SmileSimulationEmptyState } from "./SmileSimulationEmptyState";
import { SmileSimulationPatientSummary } from "./SmileSimulationPatientSummary";

type SmileSimulationPatientDetailPageProps = {
  onBack: SmileSimulationController["actions"]["goBackToHistoryList"];
  onOpenRecord: SmileSimulationController["actions"]["openRecordDetail"];
  onStartNewSimulation: SmileSimulationController["actions"]["startNewSimulation"];
  isExperience?: boolean;
  language: SmileSimulationLanguage;
  patient: NonNullable<SmileSimulationController["history"]["selectedPatient"]>;
};

export function SmileSimulationPatientDetailPage({
  onBack,
  onOpenRecord,
  onStartNewSimulation,
  isExperience = false,
  language,
  patient,
}: SmileSimulationPatientDetailPageProps) {
  const copy = getSmileSimulationCopy(language);

  return (
    <section className="flex h-full min-h-0 w-full flex-1 flex-col gap-4 sm:gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded-full border border-white/70 bg-white/90 px-4 py-2 text-sm font-semibold text-[#6b577d] shadow-[0_12px_30px_rgba(17,17,17,0.06)] transition hover:bg-white"
          >
            {copy.patientDetail.list}
          </button>
        </div>
        <button
          type="button"
          onClick={() => onStartNewSimulation(patient.id)}
          className="rounded-full bg-[#8F14E5] px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(143,20,229,0.22)] transition hover:bg-[#7d10ca]"
        >
          {copy.patientDetail.newSimulation}
        </button>
      </div>

      <div className="text-center">
        <h2 className="text-2xl font-semibold text-[#1f1230] sm:text-[1.75rem]">
          {isExperience ? "참가자 기록 상세" : copy.patientDetail.title}
        </h2>
      </div>

      <SmileSimulationPatientSummary
        caption={
          isExperience ? "Selected Participant" : copy.patientDetail.caption
        }
        isExperience={isExperience}
        language={language}
        patient={patient}
      />

      <div className="bg-white/88 flex items-center justify-between gap-3 rounded-[1.5rem] border border-white/70 p-4 shadow-[0_16px_40px_rgba(17,17,17,0.06)] backdrop-blur-sm">
        <p className="text-sm font-medium text-[#6b577d]">
          {copy.patientDetail.totalRecords}
        </p>
        <p className="text-lg font-semibold text-[#1f1230]">
          {patient.recordCount}
        </p>
      </div>

      {patient.records.length > 0 ? (
        <div className="grid gap-3 overflow-y-auto pb-2">
          {patient.records.map((record) => (
            <button
              key={record.id}
              type="button"
              onClick={() => onOpenRecord(record.id)}
              className="rounded-[1.6rem] border border-white/70 bg-white/90 p-4 text-left shadow-[0_16px_40px_rgba(17,17,17,0.08)] backdrop-blur-sm transition hover:bg-white"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[#1f1230]">
                    {formatSmileSimulationDateTime(record.createdAt)}
                  </p>
                  <p className="mt-1 text-sm text-[#6b577d]">
                    {copy.patientDetail.status} {copy.patientDetail.completed}
                  </p>
                </div>
                <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                  {copy.patientDetail.completed}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <img
                  src={record.captureImage}
                  alt={`${patient.name} before preview`}
                  className="h-28 w-full rounded-[1.25rem] object-cover"
                />
                <img
                  src={record.resultImage}
                  alt={`${patient.name} after preview`}
                  className="h-28 w-full rounded-[1.25rem] object-cover"
                />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <SmileSimulationEmptyState
          title={copy.patientDetail.emptyTitle}
          description={
            isExperience
              ? "이 참가자에 대한 첫 스마일 시뮬레이션을 시작해 보세요."
              : copy.patientDetail.emptyDescription
          }
          actionLabel={copy.patientDetail.newSimulation}
          onAction={() => onStartNewSimulation(patient.id)}
        />
      )}
    </section>
  );
}
